import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { COLUMNS } from '../data/mockOrders';
import { api, generateMockOrders } from '../services/api';
import OrderCard from './OrderCard';
import OrderModal from './OrderModal';
import StatusDropdown from './StatusDropdown';
import { cn } from '../lib/utils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useSettings } from '../context/SettingsContext';

const KanbanBoard = ({ selectedDate }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('Received');
  const [isMobile, setIsMobile] = useState(false);
  
  const { isTestData } = useSettings();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const loadOrders = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    else setRefreshing(true);
    
    try {
      let data = [];
      if (isTestData) {
        data = generateMockOrders(selectedDate, selectedDate, 25);
      } else {
        data = await api.fetchOrders(selectedDate);
      }
      setOrders(data);
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedDate, isTestData]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const interval = setInterval(() => loadOrders(true), 15000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('kanban-refresh-state', { detail: { refreshing } }));
  }, [refreshing]);

  useEffect(() => {
    const handleRefresh = () => loadOrders(true);
    window.addEventListener('kanban-refresh-trigger', handleRefresh);
    return () => window.removeEventListener('kanban-refresh-trigger', handleRefresh);
  }, [loadOrders]);

  // Слушаем эвент клика по логотипу для переключения колонки
  useEffect(() => {
    const handleSetStatus = (e) => {
      if (e.detail && e.detail.status) {
        setSelectedStatus(e.detail.status);
      }
    };
    window.addEventListener('kanban-set-status', handleSetStatus);
    return () => window.removeEventListener('kanban-set-status', handleSetStatus);
  }, []);

  const getSortedOrders = (status) => {
    return orders
      .filter(o => o.status === status)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const getOrderCounts = () => {
    const counts = {};
    Object.keys(COLUMNS).forEach(status => {
      counts[status] = getSortedOrders(status).length;
    });
    return counts;
  };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newStatus = destination.droppableId;
    const oldOrders = [...orders];

    setOrders(prev => prev.map(order => 
      (order.id === draggableId || order._id === draggableId) 
        ? { ...order, status: newStatus } 
        : order
    ));

    if (!isTestData) {
      try {
        const targetOrder = orders.find(o => (o.id === draggableId || o._id === draggableId));
        await api.updateOrderStatus(targetOrder._id || targetOrder.id, newStatus);
      } catch (error) {
        setOrders(oldOrders);
        alert('Error updating status. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-yellow-400">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mb-4" size={40} />
        <p className="text-xl font-bold uppercase tracking-widest">Loading Heroes...</p>
      </div>
    );
  }

  const orderCounts = getOrderCounts();
  const mainColumns = Object.entries(COLUMNS).filter(([key]) => key !== 'Archived');
  const archiveColumn = Object.entries(COLUMNS).find(([key]) => key === 'Archived');
  const archiveOrders = getSortedOrders('Archived');

  const MobileStatusNavButton = ({ status, icon, title }) => {
    const isActive = selectedStatus === status;
    const count = orderCounts[status] || 0;
    
    return (
      <button
        onClick={() => setSelectedStatus(status)}
        className={cn(
          "flex-1 flex flex-col items-center justify-center gap-1 transition-all relative h-full",
          isActive ? "text-yellow-400" : "text-zinc-500 hover:text-white"
        )}
      >
        <div className="relative">
          <SafeIcon icon={icon} size={22} className={cn(isActive && "scale-110 transition-transform")} />
          {count > 0 && (
            <span className={cn(
              "absolute -top-2 -right-3 min-w-[18px] h-4.5 px-1 text-[10px] font-black rounded-full flex items-center justify-center shadow-md",
              isActive ? "bg-white text-black" : "bg-yellow-400 text-black"
            )}>
              {count}
            </span>
          )}
        </div>
        <span className="text-[10px] font-black uppercase tracking-wider">{title}</span>
      </button>
    );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="h-full flex flex-col min-h-0">
        {isMobile ? (
          <div className="flex-1 flex flex-col min-h-0 relative -mx-4 -mb-4 lg:mx-0 lg:mb-0">
            {/* Dropdown at the top */}
            <div className="p-4 shrink-0 z-10 bg-black">
              <StatusDropdown 
                selectedStatus={selectedStatus} 
                onStatusChange={setSelectedStatus} 
                orderCounts={orderCounts} 
              />
            </div>
            
            {/* Scrollable List */}
            <Droppable droppableId={selectedStatus}>
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={cn(
                    "flex-1 overflow-y-auto scrollbar-hide px-4 pb-4",
                    snapshot.isDraggingOver && "bg-zinc-900/50 rounded-lg"
                  )}
                >
                  {getSortedOrders(selectedStatus).map((order, index) => (
                    <OrderCard key={order._id || order.id} order={order} index={index} onClick={setSelectedOrder} isArchive={selectedStatus === 'Archived'} />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            {/* Bottom Status Navigation */}
            <nav 
              className="flex-none bg-zinc-950 border-t border-zinc-800 z-40"
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              {/* Увеличена высота и убраны верхние/нижние паддинги для центровки */}
              <div className="flex items-stretch justify-around px-2 h-16">
                <MobileStatusNavButton status="Received" icon={FiIcons.FiInbox} title="New" />
                <MobileStatusNavButton status="Preparing" icon={FiIcons.FiClock} title="Kitchen" />
                <MobileStatusNavButton status="Delivering" icon={FiIcons.FiTruck} title="Delivery" />
                <MobileStatusNavButton status="Completed" icon={FiIcons.FiCheck} title="Done" />
              </div>
            </nav>
          </div>
        ) : (
          <div className="flex-1 flex gap-4 min-h-0">
            <div className="flex-1 flex gap-4 min-w-0">
              {mainColumns.map(([columnId, columnData]) => {
                const columnOrders = getSortedOrders(columnId);
                const isDone = columnId === 'Completed';

                return (
                  <div key={columnId} className="flex-1 min-w-0 flex flex-col">
                    <div className="mb-4 shrink-0">
                      <div className={cn("flex items-center justify-between px-4 py-3 rounded-md", isDone ? "bg-zinc-800" : "bg-yellow-400")}>
                        <h3 className={cn("font-black uppercase tracking-wide text-base truncate pr-2", isDone ? "text-zinc-400" : "text-black")}>
                          {columnData.title}
                        </h3>
                        <span className={cn("px-2 py-0.5 rounded text-sm font-black", isDone ? "bg-black text-zinc-500" : "bg-black text-yellow-400")}>
                          {columnOrders.length}
                        </span>
                      </div>
                    </div>

                    <Droppable droppableId={columnId}>
                      {(provided, snapshot) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className={cn("flex-1 rounded-lg overflow-y-auto min-h-[150px] transition-all scrollbar-hide", snapshot.isDraggingOver ? "bg-zinc-900/50 ring-2 ring-yellow-400/20" : "bg-transparent")}
                        >
                          {columnOrders.map((order, index) => (
                            <OrderCard key={order._id || order.id} order={order} index={index} onClick={setSelectedOrder} />
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>

            {archiveColumn && (
              <div className={cn("flex-shrink-0 transition-all duration-300 flex flex-col border-l border-zinc-800 pl-4", archiveOpen ? "w-1/5 min-w-[240px]" : "w-16 items-center")}>
                {archiveOpen ? (
                  <>
                    <div className="mb-4 w-full shrink-0">
                      <div className="flex items-center justify-between px-4 py-3 bg-zinc-800 rounded-md">
                        <h3 className="font-black uppercase tracking-wide text-base text-zinc-400">{archiveColumn[1].title}</h3>
                        <button onClick={() => setArchiveOpen(false)} className="text-zinc-500 hover:text-white transition-colors">
                          <SafeIcon icon={FiIcons.FiChevronsRight} size={20} />
                        </button>
                      </div>
                    </div>
                    <Droppable droppableId={archiveColumn[0]}>
                      {(provided, snapshot) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className={cn("flex-1 rounded-lg overflow-y-auto transition-all scrollbar-hide w-full", snapshot.isDraggingOver ? "bg-zinc-900/50 ring-2 ring-zinc-700" : "bg-transparent")}
                        >
                          {archiveOrders.map((order, index) => (
                            <OrderCard key={order._id || order.id} order={order} index={index} onClick={setSelectedOrder} isArchive />
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </>
                ) : (
                  <button onClick={() => setArchiveOpen(true)} className="w-12 h-12 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors group relative shrink-0">
                    <SafeIcon icon={FiIcons.FiArrowLeft} className="text-zinc-400 group-hover:text-white" size={20} />
                    {archiveOrders.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 text-black text-xs font-black rounded-full flex items-center justify-center">{archiveOrders.length}</span>}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        <OrderModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      </div>
    </DragDropContext>
  );
};

export default KanbanBoard;